import moment from 'moment';

import { Error } from '@/api/billing_error_handler';
import {
	BOOK_A_CALL_LINK,
	PLAN_DESCRIPTIONS,
	DATE_FORMAT_DISPLAY,
	DOWNGRADE_UNIT_AMOUNT,
	DOWNGRADE_SCHEDULE_TYPE,
	PLAN_DESCRIPTIONS_TITLES,
	VIRTUALLY_UNLIMITED_IDENTIFIER,
} from '@/components/Billing/billing-service.const';
import {
	Card,
	Plan,
	Price,
	Product,
	Invoice,
	PriceMap,
	PlanTypes,
	PriceTier,
	PlanGroups,
	ProductType,
	Entitlement,
	PricingType,
	BillingLevel,
	Subscription,
	BillingError,
	PaymentMethod,
	Subscriptions,
	BillingPeriod,
	PurchaseDetails,
	PricePreviewInput,
	EntitlementObject,
	BillingErrorCodes,
	ProductPricesPreview,
	DEFAULT_PRICE_PREVIEW,
	ProductInvoicePreview,
	UpgradePlanDialogProps,
	WorkflowRelatedUpgradeProps,
	PricePreviewInputParams,
} from '@/components/Billing/billing-types';
import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import { InvoiceDisplay } from '@/react_app/components/BillingUsage/InvoiceHistory/types';
import { User } from '@/store/modules/account/account-types';
import { ADMIN_ROLE, LITE_END_USER_ROLE, STANDARD_END_USER_ROLE } from '@/utils/account-service.const';
import ErrorHandlingService, { STATUS_CODES } from '@/utils/error-handling-service';
import { addBigNumberSuffix } from '@/utils/helpers';
import { capitalizeFirstWord } from '@/utils/language-service';
import { modeToParamsMap, Modes } from '@/utils/run-workflow-restriction-service';

const getTierPrice = (tier: PriceTier) => tier.flatPrice?.amount || 0;
const getSubscriptionProductType = (subscription: Subscription) => subscription.plan.product.refId;
const getPlanPricingType = (plan: Plan) => plan.pricingType;
const isPaidPlan = (plan: Plan) => getPlanPricingType(plan) === PricingType.paid;
const isEnterprisePlan = (plan: Plan) => getPlanPricingType(plan) === PricingType.custom;
const isFreePlan = (plan: Plan) => getPlanPricingType(plan) === PricingType.free;
const isPlanFreeBeta = (plan: Plan) => isFreePlan(plan) && getPlanGroup(plan) === PlanGroups.Beta;
const IsPlanRegularFree = (plan: Plan) => isFreePlan(plan) && !isPlanFreeBeta(plan);
const getPlanUsageLimit = (plan: Plan) => Math.max(...((plan.entitlements || []).map(getEntitlementUsageLimit) || 0));
const getPlanDescriptions = (plan: Plan) => PLAN_DESCRIPTIONS[getPlanType(plan)][getPlanGroup(plan)];
const getPlanDescriptionsTitle = (plan: Plan) => PLAN_DESCRIPTIONS_TITLES[getPlanType(plan)][getPlanGroup(plan)];
const getPriceAmount = (price: Price) => price.price?.amount || 0;
const isCreditsPlan = (plan: Plan) => getPlanType(plan) === PlanTypes.Credits;
const isAutomationPlan = (plan: Plan) => getPlanType(plan) === PlanTypes.Automation;
const isTeamFeaturesPlan = (plan: Plan) => getPlanType(plan) === PlanTypes.TeamFeatures;
const isCustomerFeaturesPlan = (plan: Plan) => getPlanType(plan) === PlanTypes.CustomerFeatures;
const isVirtuallyUnlimitedRefId = (refId: string) => refId.includes(VIRTUALLY_UNLIMITED_IDENTIFIER);
const isSubscriptionVirtuallyUnlimited = (subscription: Subscription) => isVirtuallyUnlimitedRefId(getSubscriptionPlanId(subscription));
const getEntitlementUsageLimit = (entitlement: Entitlement) => entitlement.usageLimit || 0;
const isPendingDowngrade = (subscription: Subscription) => !!getDowngradeUpdate(subscription);
const getSubscriptionByProductType = (subscriptions: Subscriptions, productType: ProductType) =>
	Object.values(subscriptions).find((subscription) => getSubscriptionProductType(subscription) === productType);
const getDowngradeUpdate = (subscription: Subscription) =>
	subscription.scheduledUpdates?.find(
		(update) => update.subscriptionScheduleType === DOWNGRADE_SCHEDULE_TYPE || update.subscriptionScheduleType === DOWNGRADE_UNIT_AMOUNT
	);
const getSubscriptionPlanId = (subscription: Subscription, ignoreDowngrade?: boolean) => {
	const downgradeUpdate = getDowngradeUpdate(subscription);
	const currentPlanId = subscription.plan.refId;
	if (downgradeUpdate && !ignoreDowngrade) {
		return downgradeUpdate.scheduleVariables.downgradePlanRefId || currentPlanId;
	} else {
		return currentPlanId;
	}
};
const getDowngradeUpdateUsageLimit = (subscription: Subscription, usePlanById: (planId: string) => Plan) => {
	const downgradeUpdate = getDowngradeUpdate(subscription);
	return (
		downgradeUpdate.scheduleVariables.billableFeatures?.[0]?.quantity ||
		downgradeUpdate.scheduleVariables.newUnitAmount ||
		getPlanUsageLimit(usePlanById(getSubscriptionPlanId(subscription)))
	);
};
const getSubscriptionSliderValue = (
	subscription: Subscription,
	usePlanById: (planId: string) => Plan,
	ignoreDowngrade: boolean,
	entitlement: EntitlementObject
) => {
	if (isPendingDowngrade(subscription) && !ignoreDowngrade) {
		return getDowngradeUpdateUsageLimit(subscription, usePlanById);
	}
	if (isFreePlan(subscription.plan)) {
		return getPlanUsageLimit(subscription.plan);
	}
	if (isEnterprisePlan(subscription.plan)) {
		return entitlement.entitlement.usage_limit || 0;
	}
	const usageLimit = subscription.prices[0].usageLimit || 0;
	if (!usageLimit) {
		throw new Error('No usage limit found for the given subscription');
	}
	return usageLimit;
};
const bookACall = ({ user }: { user: User }) => {
	const email = user.email;
	const accountId = String(user.profile.account.id);
	const name = `${user.first_name} ${user.last_name}`;
	const searchParams = new URLSearchParams({ name, email, workspace: accountId });
	const url = `${BOOK_A_CALL_LINK}?${searchParams.toString()}`;
	return window.open(url, '_blank');
};
const getPlanPrice = (plan: Plan, billingPeriod: BillingPeriod) => {
	const relevantPeriodPrice = plan.prices.find((price) => price.billingPeriod === billingPeriod);
	return relevantPeriodPrice ? getPriceAmount(relevantPeriodPrice) : 0;
};
const openUpgradePlanDialog = ({ planId, productId, featureId, canEditBilling, upgradeNeededExplanation }: UpgradePlanDialogProps) => {
	if (canEditBilling) {
		AppDialogManagerService.openUpgradePlanDialog({ planId, productId, featureId, upgradeNeededExplanation });
	}
	return AppDialogManagerService.openRunFailInfoDialog(modeToParamsMap()[Modes.capabilityRestrictedNotAdmin]);
};
const openWorkflowRelatedUpgradeDialog = ({
	canEditBilling,
	detail,
	reasons,
	planRequired,
	canStartTrial,
	trialEntitlements,
	relatedWorkflowVersionId,
}: WorkflowRelatedUpgradeProps) => {
	if (canStartTrial) {
		AppDialogManagerService.openSuggestPromotionalTrialDialog({
			reasons,
			planRequired,
			canStartTrial,
			trialEntitlements,
			relatedWorkflowVersionId,
			canEditBilling,
		});
	}
	return AppDialogManagerService.openPublishFailedBecauseBillingPlanDialog({
		detail,
		reasons,
		planRequired,
	});
};

const getPlanTiers = (plan: Plan, billingPeriod: BillingPeriod) => {
	const priceByPeriod = plan.prices.find((price) => price.billingPeriod === billingPeriod);
	if (!priceByPeriod) {
		throw new Error('No price found for the given billing period');
	}
	return priceByPeriod.tiers || [];
};

const getSliderFreePlanUsageLimit = (product: Product) => {
	const freePlan = product.plans.find((plan) => isFreePlan(plan));
	if (!freePlan) {
		throw new Error('No free plan found');
	}
	return getPlanUsageLimit(freePlan);
};
const buildSliderPriceMap = (product: Product, billingPeriod: BillingPeriod, enterprisePlanAmount: number) => {
	const priceMap: PriceMap = {
		[getSliderFreePlanUsageLimit(product)]: 0,
	};
	const paidPlan = product.plans.find(isPaidPlan);
	if (!paidPlan) {
		throw new Error('No paid plan found');
	}
	const tiers = getPlanTiers(paidPlan, billingPeriod);
	tiers.forEach((tier) => {
		const amount = getTierPrice(tier);
		if (amount) {
			priceMap[tier.upTo] = amount;
		}
	});

	if (!Object.keys(priceMap).includes(String(enterprisePlanAmount))) {
		priceMap[enterprisePlanAmount] = undefined;
	} else {
		const maxNumOfTasks = Math.max(...Object.keys(priceMap).map(Number));
		priceMap[String(maxNumOfTasks) + '+'] = undefined;
	}
	return priceMap;
};
const getProductAmountOfTasks = (product: Product, planGroup: PlanGroups, isVirtuallyUnlimitedPlan: boolean = false) => {
	const relevantAddOns = product.addons.filter((addOn) => {
		const refId = addOn.refId;
		if (refId.includes(planGroup)) {
			if (isVirtuallyUnlimitedPlan) {
				return isVirtuallyUnlimitedRefId(refId);
			} else {
				return !isVirtuallyUnlimitedRefId(refId);
			}
		}
		return false;
	});
	const usageLimits = relevantAddOns.map((addOn) => getEntitlementUsageLimit(addOn.entitlements[0] || {}));
	return Math.max(...usageLimits);
};
const getTasksPriceMap = (plan: Plan, billingPeriod: BillingPeriod) => {
	const priceMap: PriceMap = {};
	const tiers = getPlanTiers(plan, billingPeriod);
	tiers.forEach((tier) => {
		const amount = getTierPrice(tier);
		if (amount) {
			priceMap[tier.upTo] = amount;
		}
	});
	return priceMap;
};
const getPlanBasePrice = (plan: Plan, billingPeriod: BillingPeriod) => {
	const price = plan.prices.find((price) => price.billingPeriod === billingPeriod);
	if (!price) {
		return 0;
	}
	return getPriceAmount(price);
};
const getUsersPrice = (teamFeatureProduct: Product, plan: Plan, billingPeriod: BillingPeriod, isVirtuallyUnlimitedPlan: boolean) => {
	const addons = teamFeatureProduct.addons;
	const planGroup = getPlanGroup(plan);
	const planRelevantAddOns = addons.filter((addOn) => {
		if (addOn.refId.includes(planGroup)) {
			if (isVirtuallyUnlimitedPlan) {
				return isVirtuallyUnlimitedRefId(addOn.refId);
			} else {
				return !isVirtuallyUnlimitedRefId(addOn.refId);
			}
		}
	});
	const planAndBillingPeriodRelevantAddOns = planRelevantAddOns.map((addOn) =>
		addOn.prices.find((price: Price) => price.billingPeriod === billingPeriod)
	);
	const planBasePrice = getPlanBasePrice(plan, billingPeriod);
	// @ts-ignore
	return planAndBillingPeriodRelevantAddOns.map((price) => getPriceAmount(price) + planBasePrice).toSorted((a: number, b: number) => a - b);
};

const getCustomerFeaturesPrice = (
	teamFeatureProduct: Product,
	planGroup: PlanGroups,
	billingPeriod: BillingPeriod,
	tasksAmount: number
) => {
	const relevantPlanToGroup = teamFeatureProduct.plans.find((plan) => getPlanGroup(plan) === planGroup);
	if (!relevantPlanToGroup) {
		throw new Error('No relevant plan found for the given plan group');
	}
	const priceObjByPeriod = relevantPlanToGroup.prices.find((price) => price.billingPeriod === billingPeriod);
	if (!priceObjByPeriod) {
		return 0;
	}
	const tiers = priceObjByPeriod.tiers || [];
	const relevantTier = tiers.find((tier) => tier.upTo === tasksAmount);
	if (!relevantTier) {
		return 0;
	}
	return getTierPrice(relevantTier);
};
const getTeamFeaturesPlanPrice = (
	teamFeatureProduct: Product,
	plan: Plan,
	billingPeriod: BillingPeriod,
	amountOfStandardMembers: number,
	amountOfPremiumMembers: number
) => {
	const isVirtuallyUnlimitedPlan = isVirtuallyUnlimitedRefId(plan.refId);
	const prices = getUsersPrice(teamFeatureProduct, plan, billingPeriod, isVirtuallyUnlimitedPlan);
	if (prices.length === 0) {
		return 0;
	}
	const standardMembersPrice = prices[0];
	const premiumMembersPrice = prices[1];
	const totalStandardMembersPrice = standardMembersPrice * amountOfStandardMembers;
	const totalPremiumMembersPrice = premiumMembersPrice * amountOfPremiumMembers;
	return totalStandardMembersPrice + totalPremiumMembersPrice;
};
const openUpgradeMultiplePlansDialog = ({
	planEndDate,
	totalDueToday,
	renewableAmount,
	newPurchaseDetails,
	currentPurchaseDetails,
}: {
	planEndDate: string;
	totalDueToday: string;
	renewableAmount: string;
	newPurchaseDetails: PurchaseDetails;
	currentPurchaseDetails: PurchaseDetails;
}) => {
	AppDialogManagerService.openUpgradeMultiplePlansDialog({
		planEndDate,
		totalDueToday,
		renewableAmount,
		newPurchaseDetails,
		currentPurchaseDetails,
	});
};

const isAnnuallySubscription = (subscriptions: Subscriptions) => {
	const subscriptionsArray = Object.values(subscriptions);
	const upComingDowngrades = subscriptionsArray.map((subscription) => getDowngradeUpdate(subscription)).filter((downgrade) => downgrade);
	if (upComingDowngrades.length > 0) {
		return upComingDowngrades.some((downgrade) => downgrade.scheduleVariables.billingPeriod === BillingPeriod.annually);
	} else {
		const paidSubscriptions = subscriptionsArray.filter((subscription) => isPaidPlan(subscription.plan) && subscription.prices);
		if (paidSubscriptions.length === 0) {
			return false;
		}
		return paidSubscriptions.some((subscription) =>
			subscription.prices.some((price) => price.price?.billingPeriod === BillingPeriod.annually)
		);
	}
};

const getPlanGroup = (plan: Plan) => {
	const refId = plan.refId;
	const isVirtuallyUnlimitedPlan = isVirtuallyUnlimitedRefId(refId);
	const groupIndex = isVirtuallyUnlimitedPlan ? -2 : -1;
	const planGroup = refId.split('-').at(groupIndex) as PlanGroups;
	if (getPlanType(plan) === PlanTypes.CustomerFeatures) {
		if (planGroup === PlanGroups.Plus) {
			return PlanGroups.Engage;
		}
		if (planGroup === PlanGroups.Business) {
			return PlanGroups.Scale;
		}
	}
	return planGroup;
};

const getPlanType = (plan: Plan) => {
	const refId = plan.refId;
	const isVirtuallyUnlimitedPlan = isVirtuallyUnlimitedRefId(refId);
	const groupIndex = isVirtuallyUnlimitedPlan ? -2 : -1;
	return refId.split('-').slice(0, groupIndex).join('-') as PlanTypes;
};

const getCustomerFeatureUsageLimit = (
	customerFeatureSubscription: Subscription,
	ignoreDowngrade: boolean,
	entitlement: EntitlementObject
) => {
	const pendingDowngradeUpdate = getDowngradeUpdate(customerFeatureSubscription);
	if (pendingDowngradeUpdate && !ignoreDowngrade) {
		return (
			pendingDowngradeUpdate.scheduleVariables.newUnitAmount ||
			pendingDowngradeUpdate.scheduleVariables.billableFeatures[0]?.quantity ||
			getPlanUsageLimit(customerFeatureSubscription.plan) // for free-plan
		);
	}
	const plan = customerFeatureSubscription.plan;
	if (isFreePlan(plan)) {
		return getPlanUsageLimit(plan);
	} else if (isEnterprisePlan(plan)) {
		return entitlement.entitlement.usage_limit || 0;
	} else {
		return customerFeatureSubscription.prices[0].usageLimit || 0;
	}
};
const buildPricingPreviewKey = (pricePreviewInput: PricePreviewInput) => {
	const { billing_period: billingPeriod, product_id: productId, unit_quantity: unitQuantity, plan_id: planId } = pricePreviewInput;
	let pricingPreviewKey = `${billingPeriod}_${productId}`;
	if (planId) {
		pricingPreviewKey = `${pricingPreviewKey}_${planId}`;
	}
	if (unitQuantity) {
		pricingPreviewKey = `${pricingPreviewKey}_${unitQuantity}`;
	}
	return pricingPreviewKey;
};
const buildPricePreviewInput = ({ planId, productId, unitQuantity, billingPeriod }: PricePreviewInputParams) => {
	const pricePreviewInput: PricePreviewInput = {
		product_id: productId,
		billing_period: billingPeriod,
	};
	if (unitQuantity !== undefined) {
		pricePreviewInput.unit_quantity = unitQuantity;
	}
	if (planId) {
		pricePreviewInput.plan_id = planId;
	}
	return pricePreviewInput;
};
const extractPricePreview = (invoicePreview: ProductInvoicePreview): ProductPricesPreview => ({
	dueToday: invoicePreview.preview_subscription.immediate_invoice.total.amount,
	upComingInvoice: invoicePreview.preview_subscription.recurring_invoice.total.amount,
});
const getProductPricePreview = (pricePreview: ProductInvoicePreview | undefined) => {
	if (!pricePreview) {
		return DEFAULT_PRICE_PREVIEW;
	} else {
		return extractPricePreview(pricePreview);
	}
};

const removePlanPrefix = (planId: string) => planId.replace('plan-', '');
const dashesToSentence = (str: string) => capitalizeFirstWord(str.replace(/-/g, ' '));
const getTeamFeaturesProductTitle = (planId: string) => {
	const removedPlanPrefix = removePlanPrefix(planId);
	const planReadableSentence = dashesToSentence(removedPlanPrefix);
	return `${planReadableSentence} `;
};
const changePlusToEngage = (planId: string) => planId.replace('plus', 'Engage');
const changeBusinessToScale = (planId: string) => planId.replace('business', 'Scale');

const getCustomerFeatureProductTitle = (planId: string, quantity: number) => {
	const removedPlanPrefix = removePlanPrefix(planId);
	const planReadableSentence = dashesToSentence(removedPlanPrefix);
	const planReadableSentenceAfterChangedNames = changeBusinessToScale(changePlusToEngage(planReadableSentence));
	return `${planReadableSentenceAfterChangedNames} - ${!quantity ? 'custom' : addBigNumberSuffix(quantity)} tasks`;
};

const getAutomationProductTitle = (quantity: number) => `${addBigNumberSuffix(quantity)} Automation tasks`;
const getCreditProductTitle = (quantity: number) => `${addBigNumberSuffix(quantity)} Ply credits`;
const getPlanReadableName = ({ planId, quantity, productType }: { planId?: string; quantity?: number; productType: ProductType }) => {
	switch (productType) {
		case ProductType.TeamFeatures:
			return getTeamFeaturesProductTitle(planId as string);
		case ProductType.CustomerFeatures:
			return getCustomerFeatureProductTitle(planId as string, quantity as number);
		case ProductType.Automation:
			return getAutomationProductTitle(quantity as number);
		case ProductType.Credits:
			return getCreditProductTitle(quantity as number);
		default:
			throw new Error('Unsupported product type');
	}
};

const getRelevantPlanIdForSubscription = (subscription: Subscription) => {
	const subscriptionFutureDowngrade = getDowngradeUpdate(subscription);
	if (subscriptionFutureDowngrade) {
		return (
			subscriptionFutureDowngrade.scheduleVariables.refId || subscriptionFutureDowngrade.targetPackage?.refId || subscription.plan.refId
		);
	} else {
		return subscription.plan.refId;
	}
};
const isEntitlementUnlimited = (entitlement: EntitlementObject) => entitlement.entitlement.is_unlimited;

const getPaymentMethodCard = (paymentMethod: PaymentMethod) => {
	const { last4, brand, exp_month, exp_year } = paymentMethod.card;
	return { last4, brand, exp_month: String(exp_month), exp_year: String(exp_year) } as Card;
};
const getInvoicesDisplay = (invoices: Invoice[]) =>
	invoices.map((invoice) => {
		const { created: date, number, amount_due: amount, status, invoice_pdf: invoiceUrl } = invoice;
		const normalizedAmount = amount ? amount / 100 : 0; // amount is in cents
		const normalizedDate = date ? date * 1000 : 0; // date is in seconds
		return {
			date: moment(Number(normalizedDate)).format(DATE_FORMAT_DISPLAY),
			number: String(number),
			amount: String(normalizedAmount),
			status,
			invoiceUrl,
		} as InvoiceDisplay;
	});

const handleBillingError = ({
	error,
	userRole,
	errorCode,
	canEditBilling,
	requestAccess,
}: {
	userRole?: string;
	error: BillingError;
	canEditBilling: boolean;
	errorCode: BillingErrorCodes;
	requestAccess: unknown;
}) => {
	if (errorCode === BillingErrorCodes.LiteSharedUser) {
		return handleLiteSharedBillingError({ requestAccess });
	}
	if (errorCode === BillingErrorCodes.UsageLimitExceeded) {
		return handleOutOfTasksBillingError({ userRole });
	}
	if (errorCode === BillingErrorCodes.ContextTypeNotAllowed) {
		const minimalRoleToRunFeature = error.metadata.billing_level;
		return handleContextTypeNotAllowedBillingError({ userRole, minimalRoleToRunFeature, requestAccess });
	}
	if (errorCode === BillingErrorCodes.UpgradeSubscription) {
		const upgradeNeededExplanation = error.msg || error.detail || error.metadata.detail;
		if (!error.metadata.plan_required) {
			return;
		}

		const { product_id: productId, plan_id: planId } = error.metadata.plan_required;
		const featureId = error.metadata.reason || error.metadata.reasons?.[0];
		if (!featureId) {
			throw new Error('No featureId found in the error');
		}
		const workflowVersionRelated = error.metadata.related_workflow_version_id;
		if (workflowVersionRelated) {
			return openWorkflowRelatedUpgradeDialog({
				canEditBilling,
				detail: upgradeNeededExplanation,
				reasons: error.metadata.reasons,
				planRequired: error.metadata.plan_required,
				canStartTrial: error.metadata.can_start_trial,
				trialEntitlements: error.metadata.trial_entitlements,
				relatedWorkflowVersionId: workflowVersionRelated,
			});
		}
		return openUpgradePlanDialog({ planId, featureId, productId, canEditBilling, upgradeNeededExplanation });
	}
};

const handleLiteSharedBillingError = ({ requestAccess }: { requestAccess?: unknown }) =>
	AppDialogManagerService.openRunFailInfoDialog(modeToParamsMap(requestAccess)[Modes.liteSharedUser]);

const handleOutOfTasksBillingError = ({ userRole }: { userRole?: string }) => {
	const isAdmin = userRole === ADMIN_ROLE;
	if (isAdmin) {
		return AppDialogManagerService.openRunFailInfoDialog(modeToParamsMap()[Modes.noTasksAdmin]);
	}
	if (!isAdmin && userRole) {
		return AppDialogManagerService.openRunFailInfoDialog(modeToParamsMap()[Modes.noTasksNotAdmin]);
	}
	// no user role, it means a customer is trying to run a feature
	return AppDialogManagerService.openRunFailInfoDialog(modeToParamsMap()[Modes.customerOutOfTasks]);
};

const handleContextTypeNotAllowedBillingError = ({
	userRole,
	minimalRoleToRunFeature,
	requestAccess,
}: {
	userRole?: string;
	minimalRoleToRunFeature?: BillingLevel;
	requestAccess?: unknown;
}) => {
	const isLite = userRole === LITE_END_USER_ROLE;
	const isStandard = userRole === STANDARD_END_USER_ROLE;
	if (isLite) {
		const isStandardRequired = minimalRoleToRunFeature === BillingLevel.StandardMember;
		if (isStandardRequired) {
			return AppDialogManagerService.openRunFailInfoDialog(modeToParamsMap(requestAccess)[Modes.liteToStandard]);
		}
		return AppDialogManagerService.openRunFailInfoDialog(modeToParamsMap(requestAccess)[Modes.liteToPremium]);
	}
	if (isStandard) {
		return AppDialogManagerService.openRunFailInfoDialog(modeToParamsMap(requestAccess)[Modes.standardToPremium]);
	}
	throw new Error('Unsupported billing error handleContextTypeNotAllowedBillingError');
};

const isBillingError = (error: Error) =>
	ErrorHandlingService.extractStatusCode(error) === STATUS_CODES.FORBIDDEN &&
	Object.values(BillingErrorCodes).includes(ErrorHandlingService.extractErrorCode(error));

const BillingService = {
	bookACall,
	isPaidPlan,
	isFreePlan,
	isEnterprisePlan,
	getPlanPrice,
	getPlanGroup,
	getUsersPrice,
	isCreditsPlan,
	isBillingError,
	isPlanFreeBeta,
	getTasksPriceMap,
	isAutomationPlan,
	IsPlanRegularFree,
	getPlanUsageLimit,
	getDowngradeUpdate,
	getInvoicesDisplay,
	isPendingDowngrade,
	isTeamFeaturesPlan,
	handleBillingError,
	getPlanReadableName,
	getPlanDescriptions,
	buildSliderPriceMap,
	extractPricePreview,
	getPaymentMethodCard,
	getSubscriptionPlanId,
	openUpgradePlanDialog,
	isEntitlementUnlimited,
	buildPricePreviewInput,
	getProductPricePreview,
	buildPricingPreviewKey,
	isCustomerFeaturesPlan,
	isAnnuallySubscription,
	getProductAmountOfTasks,
	getTeamFeaturesPlanPrice,
	getPlanDescriptionsTitle,
	getCustomerFeaturesPrice,
	isVirtuallyUnlimitedRefId,
	getSubscriptionProductType,
	getSubscriptionSliderValue,
	getCustomerFeatureUsageLimit,
	getDowngradeUpdateUsageLimit,
	getSubscriptionByProductType,
	openUpgradeMultiplePlansDialog,
	isSubscriptionVirtuallyUnlimited,
	getRelevantPlanIdForSubscription,
};

export default BillingService;
